import styled from "styled-components";
import {IconButton} from "@mui/material";
import {dismissRowInlinePadding, radii, SMALL_ICON_BUTTON_PADDING_PX} from "theme/tokens";
import {linkButtonCss} from "account/purchases/UnusedPurchasesCard.styles";

/**
 * Styles for the unused-purchase note and the "using" strip in the create panel's notices slot
 * (kitecorp/transferify-ui#3004), externalized per the repo's convention.
 *
 * TEAL (`signalTint`), deliberately neither of the slot's two existing tones: blue is the verify callout's
 * "something needs your attention", gold is an offer. This is neither — it is the reader's own money,
 * already spent, and teal is what the over-limit strip already paints a settled purchase in
 * (`OverLimitNotice.styles`), so the two read as the same kind of thing.
 *
 * The 12px radius and the 12px/14px padding are the slot's box, matching the two callouts beside it. The
 * right padding reserves the pinned dismiss X's column, from the same helper every dismissible row in the
 * app corrects with (kitecorp/transferify-ui#1599).
 */
const rootInlinePaddingPx = dismissRowInlinePadding(14);
const SMALL_ICON_PX = 20;
const DISMISS_BUTTON_BOX_PX = SMALL_ICON_PX + 2 * SMALL_ICON_BUTTON_PADDING_PX;

export const NoteRoot = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 3px;
	padding: 12px ${rootInlinePaddingPx.right + DISMISS_BUTTON_BOX_PX}px 12px ${rootInlinePaddingPx.left}px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.signalTint};
	font-size: 12.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const NoteTitle = styled.span`
	color: ${({theme}) => theme.tokens.ink};
	font-weight: 700;
	font-size: 13.5px;
`;

export const NoteLinks = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0 16px;
	margin-top: 1px;
`;

/** The note's own link controls: the card's link shape, one step smaller to sit inside a strip. */
export const NoteLink = styled.button.attrs({type: "button"})<{$muted?: boolean}>`
	${linkButtonCss};
	min-height: 36px;
	font-size: 13px;
	color: ${({theme, $muted}) => ($muted ? theme.tokens.ink2 : theme.tokens.signalInk)};
	font-weight: ${({$muted}) => ($muted ? 600 : 700)};
`;

/** With several purchases the note lists each, one line per purchase, each with its own "Use it". */
export const NoteList = styled.ul`
	list-style: none;
	margin: 4px 0 0;
	padding: 0;
	display: flex;
	flex-direction: column;
`;

export const NoteItem = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;

	& + & {
		border-top: 1px solid ${({theme}) => theme.tokens.line};
	}

	> span {
		color: ${({theme}) => theme.tokens.ink};
		font-weight: 600;
		min-width: 0;
	}
`;

/**
 * Closing the note. Pinned to the top-right corner out of the flex flow, exactly as the offer callout's
 * is (kitecorp/transferify-ui#1616): as a flex child it would wrap under the text at narrow widths.
 */
export const DismissButton = styled(IconButton)`
	position: absolute;
	top: ${12 - SMALL_ICON_BUTTON_PADDING_PX}px;
	right: ${rootInlinePaddingPx.right}px;
	color: ${({theme}) => theme.tokens.ink2};

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
		background-color: ${({theme}) => theme.tokens.signalTint};
	}
`;

/** After "Use it": one line naming the purchase this send will use, and the way back. */
export const UsingRoot = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px 12px;
	padding: 9px 14px;
	border-radius: ${radii.control};
	background: ${({theme}) => theme.tokens.signalTint};
	font-size: 12.5px;
	line-height: 1.45;
	color: ${({theme}) => theme.tokens.ink2};

	> span {
		flex: 1 1 200px;
		min-width: 0;
	}

	b {
		color: ${({theme}) => theme.tokens.ink};
		font-weight: 700;
	}
`;
